import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Table,
  Badge,
  Group,
  Select,
  Loader,
  Text,
  Stack,
  Paper,
  Button,
  Checkbox,
  Modal,
  TextInput,
  Pagination,
  ActionIcon,
  Tooltip,
  Center,
  Alert,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconSearch,
  IconFilter,
  IconRefresh,
  IconSparkles,
  IconAlertCircle,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { ObservationSortHeader } from "../../../components/observations/ObservationSortHeader";
import { ObservationCategoryBadge } from "../../../components/observations/ObservationCategoryBadge";
import {
  observationsService,
  isObservationSortField,
  type ObservationListResult,
  type ObservationSortField,
  type ObservationSortOrder,
} from "../../../services/observations.service";
import {
  OBSERVATION_SEVERITY_OPTIONS,
  OBSERVATION_STATUS_OPTIONS,
  isObservationSeverity,
  isObservationStatus,
  observationSeverityColor,
  observationStatusColor,
  observationStatusLabel,
  type ObservationSeverity,
  type ObservationStatus,
} from "../../../utils/observationStatus";

const PAGE_SIZE_OPTIONS = ["20", "50", "100"];
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT_FIELD: ObservationSortField = "createdAt";
const DEFAULT_SORT_ORDER: ObservationSortOrder = "desc";
const DATE_PARAM_FORMAT = "YYYY-MM-DD";

interface PendingFilters {
  status?: ObservationStatus;
  severity?: ObservationSeverity;
  fromDate?: string;
  toDate?: string;
}

export default function ObservationsListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    backfillModalOpened,
    { open: openBackfillModal, close: closeBackfillModal },
  ] = useDisclosure(false);

  // Every list control lives in the URL, so a filtered, sorted, paged view is a
  // shareable link and survives back/forward navigation and a page reload.
  const urlSearch = searchParams.get("search") ?? "";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const limit = useMemo(() => {
    const parsed = parseInt(searchParams.get("limit") ?? "", 10);
    return PAGE_SIZE_OPTIONS.includes(String(parsed))
      ? parsed
      : DEFAULT_PAGE_SIZE;
  }, [searchParams]);

  const statusParam = searchParams.get("status");
  const severityParam = searchParams.get("severity");
  const status = isObservationStatus(statusParam) ? statusParam : undefined;
  const severity = isObservationSeverity(severityParam)
    ? severityParam
    : undefined;
  const fromDate = searchParams.get("fromDate") ?? undefined;
  const toDate = searchParams.get("toDate") ?? undefined;

  const sortByParam = searchParams.get("sortBy");
  const sortBy = isObservationSortField(sortByParam)
    ? sortByParam
    : DEFAULT_SORT_FIELD;
  const sortOrder: ObservationSortOrder =
    searchParams.get("sortOrder") === "asc" ? "asc" : "desc";

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<PendingFilters>({});

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === "") next.delete(key);
            else next.set(key, value);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Local input state keeps typing responsive; the URL is updated on debounce.
  const [inputSearch, setInputSearch] = useState(urlSearch);
  const [debouncedSearch] = useDebouncedValue(inputSearch, 300);

  const previousUrlSearch = useRef(urlSearch);
  useEffect(() => {
    if (urlSearch !== previousUrlSearch.current) {
      previousUrlSearch.current = urlSearch;
      setInputSearch(urlSearch);
    }
  }, [urlSearch]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    updateParams({ search: debouncedSearch || null, page: null });
  }, [debouncedSearch, updateParams]);

  const { data, isLoading, isFetching, error, refetch } =
    useQuery<ObservationListResult>({
      queryKey: [
        "observations",
        {
          page,
          limit,
          urlSearch,
          status,
          severity,
          fromDate,
          toDate,
          sortBy,
          sortOrder,
        },
      ],
      queryFn: () =>
        observationsService.list({
          page,
          limit,
          search: urlSearch || undefined,
          status,
          severity,
          fromDate,
          toDate,
          sortBy,
          sortOrder,
        }),
      placeholderData: (previous) => previous,
    });

  const observations = useMemo(() => data?.observations ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / limit) : 0),
    [total, limit]
  );
  const rangeStart = useMemo(
    () => (total === 0 ? 0 : (page - 1) * limit + 1),
    [total, page, limit]
  );
  const rangeEnd = useMemo(
    () => Math.min(page * limit, total),
    [page, limit, total]
  );

  const hasActiveFilters = useMemo(
    () => !!(status || severity || fromDate || toDate),
    [status, severity, fromDate, toDate]
  );

  const selectedOnPage = useMemo(
    () => observations.filter((o) => selectedIds.has(o.id)),
    [observations, selectedIds]
  );
  const allSelected = useMemo(
    () =>
      observations.length > 0 && selectedOnPage.length === observations.length,
    [observations, selectedOnPage]
  );
  const someSelected = useMemo(
    () =>
      selectedOnPage.length > 0 && selectedOnPage.length < observations.length,
    [observations, selectedOnPage]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputSearch(event.currentTarget.value);
    },
    []
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      updateParams({ page: nextPage > 1 ? String(nextPage) : null });
      setSelectedIds(new Set());
    },
    [updateParams]
  );

  const handleLimitChange = useCallback(
    (value: string | null) => {
      updateParams({
        limit: value && Number(value) !== DEFAULT_PAGE_SIZE ? value : null,
        page: null,
      });
      setSelectedIds(new Set());
    },
    [updateParams]
  );

  const handleSort = useCallback(
    (field: ObservationSortField) => {
      // Re-clicking the active column flips direction; a new column starts
      // descending, which is what people expect from dates and severity.
      const nextOrder: ObservationSortOrder =
        sortBy === field && sortOrder === "desc" ? "asc" : "desc";
      updateParams({
        sortBy: field === DEFAULT_SORT_FIELD ? null : field,
        sortOrder: nextOrder === DEFAULT_SORT_ORDER ? null : nextOrder,
        page: null,
      });
    },
    [sortBy, sortOrder, updateParams]
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleOpenFilterModal = useCallback(() => {
    setPendingFilters({ status, severity, fromDate, toDate });
    openFilterModal();
  }, [status, severity, fromDate, toDate, openFilterModal]);

  const handlePendingStatusChange = useCallback((value: string | null) => {
    setPendingFilters((prev) => ({
      ...prev,
      status: isObservationStatus(value) ? value : undefined,
    }));
  }, []);

  const handlePendingSeverityChange = useCallback((value: string | null) => {
    setPendingFilters((prev) => ({
      ...prev,
      severity: isObservationSeverity(value) ? value : undefined,
    }));
  }, []);

  const handlePendingDateChange = useCallback(
    (range: [string | null, string | null]) => {
      setPendingFilters((prev) => ({
        ...prev,
        fromDate: range[0]
          ? dayjs(range[0]).format(DATE_PARAM_FORMAT)
          : undefined,
        toDate: range[1]
          ? dayjs(range[1]).format(DATE_PARAM_FORMAT)
          : undefined,
      }));
    },
    []
  );

  const handleApplyFilters = useCallback(() => {
    updateParams({
      status: pendingFilters.status ?? null,
      severity: pendingFilters.severity ?? null,
      fromDate: pendingFilters.fromDate ?? null,
      toDate: pendingFilters.toDate ?? null,
      page: null,
    });
    setSelectedIds(new Set());
    closeFilterModal();
  }, [pendingFilters, updateParams, closeFilterModal]);

  const handleClearPendingFilters = useCallback(() => {
    setPendingFilters({});
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setPendingFilters({});
    updateParams({
      status: null,
      severity: null,
      fromDate: null,
      toDate: null,
      page: null,
    });
    setSelectedIds(new Set());
  }, [updateParams]);

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.currentTarget;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        observations.forEach((o) => {
          if (checked) next.add(o.id);
          else next.delete(o.id);
        });
        return next;
      });
    },
    [observations]
  );

  const handleSelectOne = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.currentTarget;
      const { id } = event.currentTarget.dataset;
      if (!id) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (checked) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    []
  );

  const handleRowCheckboxClick = useCallback(
    (event: React.MouseEvent<HTMLTableCellElement>) => {
      event.stopPropagation();
    },
    []
  );

  const handleApplyBulkStatus = useCallback(async () => {
    if (!isObservationStatus(bulkStatus) || selectedIds.size === 0) return;
    setIsApplying(true);
    const ids = Array.from(selectedIds);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => observationsService.updateStatus(id, bulkStatus))
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;
      const firstFailure = results.find(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      const allFailed = succeeded === 0;

      notifications.show({
        title: allFailed ? "Update failed" : "Status updated",
        message: allFailed
          ? firstFailure?.reason instanceof Error
            ? firstFailure.reason.message
            : "None of the selected observations could be updated."
          : `Updated ${succeeded} observation${succeeded === 1 ? "" : "s"}${
              failed > 0 ? `, ${failed} failed` : ""
            }`,
        color: allFailed ? "red" : failed > 0 ? "yellow" : "green",
      });

      if (succeeded > 0) {
        await queryClient.invalidateQueries({ queryKey: ["observations"] });
        setSelectedIds(new Set());
        setBulkStatus(null);
      }
    } finally {
      setIsApplying(false);
    }
  }, [bulkStatus, selectedIds, queryClient]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setBulkStatus(null);
  }, []);

  const handleConfirmBackfill = useCallback(async () => {
    setIsBackfilling(true);
    try {
      const updatedCount = await observationsService.backfill();
      notifications.show({
        title: "Backfill started",
        message: `Queued ${updatedCount} observation(s) for AI analysis.`,
        color: "blue",
      });
      await queryClient.invalidateQueries({ queryKey: ["observations"] });
      closeBackfillModal();
    } catch (err) {
      notifications.show({
        title: "Backfill failed",
        message:
          err instanceof Error
            ? err.message
            : "Couldn't start the analysis run.",
        color: "red",
      });
    } finally {
      setIsBackfilling(false);
    }
  }, [queryClient, closeBackfillModal]);

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      const { id } = event.currentTarget.dataset;
      if (id) navigate(`/admin/observations/${id}`);
    },
    [navigate]
  );

  const pendingDateRange = useMemo<[string | null, string | null]>(
    () => [pendingFilters.fromDate ?? null, pendingFilters.toDate ?? null],
    [pendingFilters.fromDate, pendingFilters.toDate]
  );

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Observations</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Review user-submitted observations, triage their status, and
                investigate AI-flagged issues.
              </Text>
            </div>
            <Button
              variant="light"
              leftSection={<IconSparkles size={16} />}
              onClick={openBackfillModal}
              loading={isBackfilling}
            >
              Run AI analysis on older observations
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by OBS-#, reporter, title, or summary..."
                  value={inputSearch}
                  onChange={handleSearchChange}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Tooltip label="Refresh">
                    <ActionIcon
                      variant="subtle"
                      onClick={handleRefresh}
                      loading={isFetching}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {status && (
                      <Badge
                        variant="light"
                        color={observationStatusColor(status)}
                      >
                        {observationStatusLabel(status)}
                      </Badge>
                    )}
                    {severity && (
                      <Badge
                        variant="light"
                        color={observationSeverityColor(severity)}
                      >
                        {severity}
                      </Badge>
                    )}
                    {(fromDate || toDate) && (
                      <Badge variant="light" color="violet">
                        {fromDate ? dayjs(fromDate).format("MMM D") : "…"}
                        {" – "}
                        {toDate ? dayjs(toDate).format("MMM D") : "…"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {total} observation{total !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <Paper withBorder p="sm">
              <Group align="flex-end" gap="sm" wrap="wrap">
                <Select
                  label="Set status to"
                  placeholder="Select status"
                  value={bulkStatus}
                  onChange={setBulkStatus}
                  data={OBSERVATION_STATUS_OPTIONS}
                  w={220}
                />
                <Button
                  onClick={handleApplyBulkStatus}
                  disabled={!bulkStatus || isApplying}
                  loading={isApplying}
                >
                  Apply to {selectedIds.size} selected
                </Button>
                <Button
                  variant="subtle"
                  color="gray"
                  onClick={handleClearSelection}
                  disabled={isApplying}
                >
                  Clear selection
                </Button>
                <Text size="sm" c="dimmed" ml="auto">
                  {selectedIds.size} selected
                </Text>
              </Group>
            </Paper>
          )}

          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              title="Couldn't load observations"
              role="alert"
            >
              {error instanceof Error
                ? error.message
                : "Something went wrong loading this list."}
            </Alert>
          )}

          {/* Table */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Observations ({total})
                </Text>
                <Group gap="md">
                  <Select
                    label={undefined}
                    aria-label="Rows per page"
                    value={String(limit)}
                    onChange={handleLimitChange}
                    data={PAGE_SIZE_OPTIONS}
                    w={100}
                    size="sm"
                  />
                  {total > 0 && (
                    <Text size="sm" c="dimmed">
                      Showing {rangeStart}–{rangeEnd} of {total}
                    </Text>
                  )}
                  {totalPages > 1 && (
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={handlePageChange}
                      size="sm"
                    />
                  )}
                </Group>
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : observations.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || urlSearch
                    ? "No observations match the current filters"
                    : "No observations found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }}>
                        <Checkbox
                          aria-label="Select all observations on this page"
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={handleSelectAll}
                        />
                      </Table.Th>
                      <ObservationSortHeader
                        field="displayNumber"
                        label="#"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        width={100}
                      />
                      <ObservationSortHeader
                        field="reporterName"
                        label="Reporter"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      />
                      <ObservationSortHeader
                        field="title"
                        label="Title"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      />
                      <ObservationSortHeader
                        field="status"
                        label="Status"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        width={140}
                      />
                      <ObservationSortHeader
                        field="severity"
                        label="Severity"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        width={110}
                      />
                      <Table.Th style={{ width: 140 }}>Category</Table.Th>
                      <ObservationSortHeader
                        field="createdAt"
                        label="Submitted"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        width={180}
                      />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {observations.map((obs) => (
                      <Table.Tr
                        key={obs.id}
                        data-id={obs.id}
                        style={{ cursor: "pointer" }}
                        onClick={handleRowClick}
                      >
                        <Table.Td onClick={handleRowCheckboxClick}>
                          <Checkbox
                            aria-label={`Select observation ${
                              obs.displayCode ?? obs.id
                            }`}
                            data-id={obs.id}
                            checked={selectedIds.has(obs.id)}
                            onChange={handleSelectOne}
                          />
                        </Table.Td>
                        <Table.Td>
                          {obs.displayCode ? (
                            <Text size="sm" fw={600} ff="monospace">
                              {obs.displayCode}
                            </Text>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {obs.reporterName}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {obs.reporterEmail}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {obs.title ? (
                            <div>
                              <Text size="sm" fw={500} lineClamp={1}>
                                {obs.title}
                              </Text>
                              {obs.summary && (
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {obs.summary}
                                </Text>
                              )}
                            </div>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={observationStatusColor(obs.status)}
                            variant="light"
                            size="sm"
                          >
                            {observationStatusLabel(obs.status)}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {obs.severity ? (
                            <Badge
                              color={observationSeverityColor(obs.severity)}
                              variant="light"
                              size="sm"
                            >
                              {obs.severity}
                            </Badge>
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <ObservationCategoryBadge category={obs.aiCategory} />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {dayjs(obs.createdAt).format("MMM D, YYYY h:mm A")}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Observations"
          size="md"
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="Any status"
              data={OBSERVATION_STATUS_OPTIONS}
              value={pendingFilters.status ?? null}
              onChange={handlePendingStatusChange}
              clearable
            />
            <Select
              label="Severity"
              placeholder="Any severity"
              data={OBSERVATION_SEVERITY_OPTIONS}
              value={pendingFilters.severity ?? null}
              onChange={handlePendingSeverityChange}
              clearable
            />
            <DatePickerInput
              type="range"
              label="Date range"
              placeholder="Pick date range"
              value={pendingDateRange}
              onChange={handlePendingDateChange}
              clearable
            />
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Backfill Confirmation Modal */}
        <Modal
          opened={backfillModalOpened}
          onClose={closeBackfillModal}
          title="Run AI analysis on older observations"
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text size="sm">
              This queues every observation that hasn&apos;t been analyzed yet.
              It may take several minutes and will consume API credits.
            </Text>
            <Group justify="flex-end" mt="md" gap="xs">
              <Button
                variant="default"
                onClick={closeBackfillModal}
                disabled={isBackfilling}
              >
                Cancel
              </Button>
              <Button onClick={handleConfirmBackfill} loading={isBackfilling}>
                Run analysis
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
